import { ActionReducerMapBuilder, Draft, PayloadAction } from '@reduxjs/toolkit'
import {
  DataList,
  saveErrorInState,
  dismissError as commonDismissError,
  closeAlert as commonCloseAlert,
  setErrorAlert
} from '../common'
import type { DownloadAction, PageState } from './types'
import type {
  fetchPages,
  createPage,
  modifyPage,
  copyPage,
  deletePage,
  fetchLayoutResult,
  exportLayout
} from './pageAPI'

export const downloadFulfilled = (_state: unknown, action: DownloadAction): void => {
  const { fileName } = action.meta.arg
  const mimeType = 'application/zip'
  const data = new window.Blob([action.payload], { type: mimeType })
  const url = window.URL.createObjectURL(data)
  const link = document.createElement('a')
  document.body.appendChild(link)
  link.href = url
  link.setAttribute('download', fileName)
  link.click()
  document.body.removeChild(link)
}

export const selectIdReducer = (state: Draft<PageState>, action: PayloadAction<number>) => {
  state.selectedIds = Array.from(new Set(state.selectedIds).add(action.payload))
}

export const unselectIdReducer = (state: Draft<PageState>, action: PayloadAction<number>) => {
  const set = new Set(state.selectedIds)
  set.delete(action.payload)
  state.selectedIds = Array.from(set)
}

export const openModalReducer = (state: Draft<PageState>, action: PayloadAction<string | null>) => {
  state.openedModal = action.payload
}

export const closeModalReducer = (state: Draft<PageState>) => {
  state.openedModal = null
  state.alert = null
}

export const setSearchConditionReducer = (
  state: Draft<PageState>,
  action: PayloadAction<{ name: string; value: unknown }>
) => {
  const { name, value } = action.payload
  state.searchCondition[name] = value
}

export const clearLayoutResultReducer = (state: Draft<PageState>) => {
  delete state.layoutResult
}

export const buildExtraReducers = (
  builder: ActionReducerMapBuilder<PageState>,
  thunks: {
    fetchPages: typeof fetchPages
    createPage: typeof createPage
    modifyPage: typeof modifyPage
    copyPage: typeof copyPage
    deletePage: typeof deletePage
    fetchLayoutResult: typeof fetchLayoutResult
    exportLayout: typeof exportLayout
  }
) =>
  builder
    .addCase(thunks.fetchPages.pending, DataList.fetchPending)
    .addCase(thunks.fetchPages.fulfilled, DataList.fetchFulfilled)
    .addCase(thunks.fetchPages.rejected, saveErrorInState(DataList.fetchRejected))
    .addCase(thunks.createPage.pending, (state) => {
      state.fetching = true
    })
    .addCase(thunks.createPage.fulfilled, (state) => {
      state.fetching = false
    })
    .addCase(
      thunks.createPage.rejected,
      setErrorAlert((state) => {
        state.fetching = false
      })
    )
    .addCase(thunks.modifyPage.pending, (state) => {
      state.fetching = true
    })
    .addCase(thunks.modifyPage.fulfilled, (state) => {
      state.fetching = false
    })
    .addCase(thunks.modifyPage.rejected, (state, action) => {
      state.fetching = false
      const error = action.payload as { message: string } | undefined
      if (error !== undefined) {
        state.alert = {
          show: true,
          variant: 'danger',
          alertBody: error.message
        }
      }
    })
    .addCase(thunks.copyPage.pending, (state) => {
      state.fetching = true
    })
    .addCase(thunks.copyPage.fulfilled, (state) => {
      state.fetching = false
    })
    .addCase(
      thunks.copyPage.rejected,
      setErrorAlert((state) => {
        state.fetching = false
      })
    )
    .addCase(thunks.deletePage.pending, (state) => {
      state.fetching = true
    })
    .addCase(thunks.deletePage.fulfilled, (state) => {
      state.fetching = false
      state.selectedIds = []
    })
    .addCase(
      thunks.deletePage.rejected,
      saveErrorInState((state) => {
        state.fetching = false
      })
    )
    .addCase(thunks.fetchLayoutResult.pending, (state) => {
      state.fetching = true
    })
    .addCase(thunks.fetchLayoutResult.fulfilled, (state, action) => {
      state.layoutResult = action.payload
      state.fetching = false
    })
    .addCase(
      thunks.fetchLayoutResult.rejected,
      saveErrorInState((state) => {
        state.fetching = false
      })
    )
    .addCase(thunks.exportLayout.pending, (state) => {
      state.fetching = true
    })
    .addCase(thunks.exportLayout.fulfilled, (state, action) => {
      downloadFulfilled(state, action as unknown as DownloadAction)
      state.fetching = false
    })
    .addCase(
      thunks.exportLayout.rejected,
      saveErrorInState((state) => {
        state.fetching = false
      })
    )

export { commonDismissError as dismissErrorReducer, commonCloseAlert as closeAlertReducer }
